// src/components/LoginPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { getOrCreateAppUser } from '../utils/supabaseUtils';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  Button, 
  Input, 
  FormControl, 
  FormLabel,
  Divider,
  Stack,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  useToast,
  Flex,
  VStack
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa';

const LoginPage = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'magic'

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      console.log('Handling OAuth callback...');
      console.log('Current location:', window.location.href);
      
      // Check for authorization code in URL
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams.get('code');
      
      if (code) {
        console.log('Authorization code found in URL');
        setIsLoading(true);
        try {
          // Get the current session to ensure we have the code verifier
          const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            console.error('Error getting current session:', sessionError);
            setError(sessionError.message);
            return;
          }

          console.log('Current session:', currentSession);

          // Exchange the code for a session
          const { data: { session }, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('Session exchange error:', exchangeError);
            setError(exchangeError.message);
            return;
          }

          if (session) {
            console.log('Session established:', session);
            try {
              // Create or get app user
              console.log('Creating/getting app user...');
              await getOrCreateAppUser();
              console.log('App user created/retrieved successfully');
              
              if (setIsAuthenticated) setIsAuthenticated(true);
              
              // Clear the URL parameters
              window.history.replaceState({}, document.title, window.location.pathname);
              
              // Navigate to dashboard
              navigate('/dashboard', { replace: true });
            } catch (error) {
              console.error('Error creating app user:', error);
              // Still navigate to dashboard even if profile creation fails
              window.history.replaceState({}, document.title, window.location.pathname);
              navigate('/dashboard', { replace: true });
            }
          }
        } catch (error) {
          console.error('Error handling OAuth callback:', error);
          setError('Authentication failed. Please try again.');
        } finally {
          setIsLoading(false);
        }
      } else {
        // Check for existing session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError.message);
          return;
        }

        if (session) {
          console.log('Existing session found:', session);
          if (setIsAuthenticated) setIsAuthenticated(true);
          navigate('/dashboard', { replace: true });
        } else {
          console.log('No session found');
        }
      }
    };

    handleOAuthCallback();
  }, [navigate, setIsAuthenticated]);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Get the current origin (either localhost:3000 or playmakerpics.vercel.app)
      const currentOrigin = window.location.origin;
      console.log('Initiating Google login with origin:', currentOrigin);
      
      // For localhost, we'll use the local URL to maintain PKCE state
      const redirectTo = currentOrigin === 'http://localhost:3000' 
        ? 'http://localhost:3000/login'
        : `${currentOrigin}/login`;
      
      console.log('Using redirect URL:', redirectTo);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { 
          redirectTo,
          scopes: 'profile email',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        },
      });
      
      if (error) {
        console.error('Google login error:', error);
        setError(error.message);
      } else {
        console.log('Google login initiated successfully');
      }
    } catch (error) {
      console.error('Unexpected error during Google login:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) {
        setError(error.message);
      } else {
        toast({
          title: "Magic link sent!",
          description: "Check your email for the login link.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
        setEmail('');
      }
    } catch (error) {
      setError('Failed to send magic link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        setError(error.message);
        return;
      }
      
      if (data.session) {
        try {
          // Create or get app user (same as Google login)
          console.log('Creating/getting app user...');
          await getOrCreateAppUser();
          console.log('App user created/retrieved successfully');
          
          if (setIsAuthenticated) setIsAuthenticated(true);
          
          // Navigate to dashboard
          navigate('/dashboard', { replace: true });
        } catch (appUserError) {
          console.error('Error creating app user:', appUserError);
          // Still navigate to dashboard even if profile creation fails
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (err) {
      console.error('Unexpected error during login:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleLoginMethod = () => setLoginMethod(loginMethod === 'password' ? 'magic' : 'password');

  return (
    <Box bg="bg.primary" minH="100vh" py={12} px={4}>
      <Container maxW="md" bg="white" p={8} borderRadius="lg" boxShadow="sm" borderWidth="1px" borderColor="neutral.300">
        <VStack spacing={8} align="stretch">
          <Box textAlign="center">
            <Heading size="lg" fontWeight="600" color="text.primary" letterSpacing="-0.02em">
              PlayMaker Pics
            </Heading>
            <Text mt={2} color="text.secondary" fontSize="md">
              Sign in to continue
            </Text>
          </Box>
          
          <Button
            leftIcon={<FaGoogle />}
            onClick={handleGoogleLogin}
            colorScheme="red"
            variant="outline"
            size="lg"
            isLoading={isLoading && !email && !password}
            loadingText="Connecting"
            boxShadow="sm"
            fontWeight="500"
            borderRadius="md"
            isFullWidth
          >
            Continue with Google
          </Button>
          
          <Flex align="center">
            <Divider flex="1" borderColor="neutral.300" />
            <Text px={3} color="text.light" fontSize="sm" fontWeight="medium">OR</Text>
            <Divider flex="1" borderColor="neutral.300" />
          </Flex>
          
          {loginMethod === 'password' ? (
            <form onSubmit={handleEmailPasswordLogin}>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium" color="text.secondary">Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    size="lg"
                    borderRadius="md"
                    borderColor="neutral.300"
                    _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px #D97C2B" }}
                    required
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium" color="text.secondary">Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      size="lg"
                      borderRadius="md"
                      borderColor="neutral.300"
                      _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px #D97C2B" }}
                      required
                    />
                    <InputRightElement h="full">
                      <IconButton
                        variant="ghost"
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                        size="sm"
                        color="text.light"
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="orange"
                  bg="brand.500"
                  _hover={{ bg: "brand.600" }}
                  size="lg"
                  isLoading={isLoading}
                  loadingText="Signing in"
                  fontWeight="500"
                  borderRadius="md"
                  mt={2}
                >
                  Sign in
                </Button>
              </Stack>
            </form>
          ) : (
            <form onSubmit={handleMagicLink}>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium" color="text.secondary">Email</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    size="lg"
                    borderRadius="md"
                    borderColor="neutral.300"
                    _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px #D97C2B" }}
                    required
                  />
                </FormControl>
                
                <Button
                  type="submit"
                  colorScheme="orange"
                  bg="brand.500"
                  _hover={{ bg: "brand.600" }}
                  size="lg"
                  isLoading={isLoading}
                  loadingText="Sending"
                  fontWeight="500"
                  borderRadius="md"
                  mt={2}
                >
                  Send Magic Link
                </Button>
              </Stack>
            </form>
          )}
          
          <Button 
            variant="link" 
            color="brand.500" 
            onClick={toggleLoginMethod}
            alignSelf="center"
            fontWeight="medium"
            size="sm"
          >
            {loginMethod === 'password' 
              ? "Sign in with magic link instead" 
              : "Sign in with password instead"}
          </Button>
          
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}
          
          <Flex justifyContent="center" mt={4}>
            <Text color="text.secondary" fontSize="sm">
              Don't have an account?{" "}
              <Link to="/register" style={{ color: "#D97C2B", fontWeight: 500 }}>
                Sign up
              </Link>
            </Text>
          </Flex>
        </VStack>
      </Container>
    </Box>
  );
};

export default LoginPage;
